import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CloudLightning,
  RefreshCw,
  Wind,
  Droplets,
  Zap,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  Gauge,
  ChevronDown,
  Snowflake,
  Waves,
  TrendingUp,
  TrendingDown,
  Minus,
  Skull,
  Search,
  MapPin,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  getStormForecast,
  MODELS,
  MODEL_META,
  type ModelId,
  type StormForecastResponse,
  type StormSample,
} from "@/lib/storm-forecast.functions";

export const Route = createFileRoute("/demo/burze-stanica")({
  head: () => ({
    meta: [
      { title: "Prognoza burzowa Stanica — model ICON-D2 + ICON-EU + ECMWF" },
      {
        name: "description",
        content:
          "Live nowcasting burzowy dla Stanicy (gm. Pilchowice) i okolic Gliwic. Trzy modele numeryczne (ICON-D2 2.2 km, ICON-EU 7 km, ECMWF IFS) + autorska formuła ryzyka konwekcyjnego.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BurzePage,
});

function BurzePage() {
  const [location, setLocation] = useState({ lat: 50.161, lon: 18.583, name: "Stanica" });

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["storm-forecast", location.lat, location.lon],
    queryFn: async () => await getStormForecast(location),
    refetchInterval: 10 * 60 * 1000,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return (
    <main className="relative min-h-screen overflow-x-clip bg-[#04060a] text-white">
      {/* tło */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="absolute -right-40 top-1/3 h-[500px] w-[500px] rounded-full bg-fuchsia-500/10 blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-6 md:pt-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300 backdrop-blur-xl transition hover:border-white/20 hover:text-white"
          >
            <ArrowLeft size={14} />
            Demo
          </Link>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-slate-200 backdrop-blur-xl transition hover:border-white/20 disabled:opacity-60"
          >
            <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Pobieram…" : "Odśwież"}
          </button>
        </div>

        <header className="mt-8 md:mt-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs uppercase tracking-wider text-cyan-300 backdrop-blur-xl">
            <CloudLightning size={12} />
            Nowcast burzowy · {location.name} + okolice
          </div>
          <h1 className="mt-4 text-balance text-4xl font-black leading-[1.05] tracking-tight md:text-6xl">
            Prognoza burzowa{" "}
            <span className="bg-gradient-to-r from-cyan-300 via-sky-400 to-fuchsia-400 bg-clip-text text-transparent">
              live
            </span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-base">
            Trzy modele numeryczne pobierane na bieżąco z Open-Meteo. Punkt centralny: {location.name} (
            {location.lat.toFixed(3)}° N · {location.lon.toFixed(3)}° E) + 4 punkty otaczające.
          </p>
          <div className="mt-6 w-full max-w-md">
            <LocationSearch onSelect={setLocation} />
          </div>
          {dataUpdatedAt > 0 && (
            <p className="mt-4 text-xs text-slate-500">
              Ostatnie pobranie: {new Date(dataUpdatedAt).toLocaleTimeString("pl-PL")} · auto-odświeżanie co 10 min
            </p>
          )}
        </header>

        {isLoading && <SkeletonBlock />}
        {isError && (
          <div className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-200">
            Nie udało się pobrać danych modeli. Spróbuj odświeżyć.
          </div>
        )}

        {data && (
          <>
            <EnsembleStrip data={data} />
            <LiveStormVerificationV2 obs={data.currentObs} />
            <StormAnalysisDashboard data={data} />

            {/* NOWY MODUŁ RADARU */}
            <LiveRadarWidget lat={location.lat} lon={location.lon} name={location.name} />

            <ModelsTable data={data} />
            <PointsGrid data={data} />
            <FormulaSection />
          </>
        )}
      </div>
      <StatusIndicator
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        hasData={!!data}
        onRetry={() => refetch()}
      />
    </main>
  );
}

/* ───────── Wskaźnik statusu wczytywania ───────── */
function StatusIndicator({
  isLoading,
  isFetching,
  isError,
  error,
  hasData,
  onRetry,
}: {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  hasData: boolean;
  onRetry: () => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [showOk, setShowOk] = useState(false);

  useEffect(() => {
    if (!isFetching && hasData && !isError) {
      setShowOk(true);
      const t = setTimeout(() => setShowOk(false), 2200);
      return () => clearTimeout(t);
    }
  }, [isFetching, hasData, isError]);

  useEffect(() => {
    if (isError) setDismissed(false);
  }, [isError]);

  const visible = isLoading || isFetching || (isError && !dismissed) || showOk;
  if (!visible) return null;

  const state: "loading" | "error" | "ok" = isError ? "error" : isLoading || isFetching ? "loading" : "ok";

  const cfg = {
    loading: {
      icon: <Loader2 size={14} className="animate-spin" />,
      title: isLoading ? "Wczytywanie modeli…" : "Odświeżam dane…",
      sub: "ICON-D2 · ICON-EU · ECMWF",
      ring: "border-cyan-400/30 bg-cyan-500/10",
      dot: "bg-cyan-400",
      text: "text-cyan-100",
    },
    error: {
      icon: <AlertTriangle size={14} />,
      title: "Błąd pobierania danych",
      sub:
        (error instanceof Error && error.message) || "Open-Meteo nie odpowiada (możliwy limit 429). Spróbuj ponownie.",
      ring: "border-red-400/30 bg-red-500/10",
      dot: "bg-red-400",
      text: "text-red-100",
    },
    ok: {
      icon: <CheckCircle2 size={14} />,
      title: "Dane aktualne",
      sub: "Wszystkie modele załadowane",
      ring: "border-emerald-400/30 bg-emerald-500/10",
      dot: "bg-emerald-400",
      text: "text-emerald-100",
    },
  }[state];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 max-w-[min(92vw,340px)] animate-in fade-in slide-in-from-bottom-2"
    >
      <div
        className={`flex items-start gap-3 rounded-2xl border ${cfg.ring} px-4 py-3 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.4)]`}
      >
        <span className="relative mt-1 flex h-2 w-2 shrink-0">
          {state === "loading" && (
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${cfg.dot} opacity-60`} />
          )}
          <span className={`relative inline-flex h-2 w-2 rounded-full ${cfg.dot}`} />
        </span>
        <div className="min-w-0 flex-1">
          <div className={`flex items-center gap-2 text-xs font-semibold ${cfg.text}`}>
            {cfg.icon}
            <span className="truncate">{cfg.title}</span>
          </div>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-300/80 line-clamp-2">{cfg.sub}</p>
          {state === "error" && (
            <button
              onClick={onRetry}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-white/15"
            >
              <RefreshCw size={11} />
              Spróbuj ponownie
            </button>
          )}
        </div>
        {(state === "error" || state === "ok") && (
          <button
            onClick={() => setDismissed(true)}
            aria-label="Zamknij"
            className="rounded-full p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function formatCountdown(sec: number): string {
  if (sec <= 0) return "0s";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${String(mm).padStart(2, "0")}m`;
}

/* ───────── Ensemble (15 / 30 / 60 / 120) ───────── */
function EnsembleStrip({ data }: { data: StormForecastResponse }) {
  return (
    <section className="mt-10">
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Konsensus modeli · Stanica</h2>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {data.ensemble.map((e) => (
          <div
            key={e.horizonMin}
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
          >
            <div
              className="absolute inset-x-0 bottom-0 h-1 transition-all"
              style={{
                width: `${e.risk}%`,
                background: riskGradient(e.risk),
              }}
            />
            <div className="text-[11px] uppercase tracking-wider text-slate-400">+{e.horizonMin} min</div>
            <div className="mt-2 flex items-end gap-1">
              <span className="text-5xl font-black tabular-nums" style={{ color: riskColor(e.risk) }}>
                {e.risk}
              </span>
              <span className="pb-1.5 text-xs text-slate-500">/100</span>
            </div>
            <div className="mt-1 text-sm font-medium" style={{ color: riskColor(e.risk) }}>
              {e.label}
            </div>
            <div className="mt-3 space-y-1 text-[11px] text-slate-400">
              {e.perModel.map((pm) => (
                <div key={pm.model} className="flex items-center justify-between">
                  <span>{MODEL_META[pm.model].label}</span>
                  <span className="tabular-nums">{pm.sample ? `${pm.risk}` : "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────── Główny dashboard: szansa vs siła + zagrożenia ───────── */
/* ───────── Główny dashboard: szansa vs siła + zagrożenia ───────── */
function StormAnalysisDashboard({ data }: { data: StormForecastResponse }) {
  let maxRisk = 0,
    maxHail = 0,
    maxTornado = 0,
    maxFlood = 0;

  // Przeszukujemy wszystkie wygenerowane punkty (Center, North, South, East, West)
  for (const p of data.points) {
    // NAPRAWA: Kaskada modeli. D2 -> EU -> ECMWF. Zawsze znajdzie jakieś dane!
    const samples = p.models.icon_d2?.length
      ? p.models.icon_d2
      : p.models.icon_eu?.length
        ? p.models.icon_eu
        : (p.models.ecmwf_ifs025 ?? []);

    for (const s of samples) {
      // Skanujemy najbliższe 2 godziny
      if (s.offsetMin >= 0 && s.offsetMin <= 120 && s.isValid) {
        maxRisk = Math.max(maxRisk, s.risk);
        maxHail = Math.max(maxHail, s.threats?.hail ?? 0);
        maxTornado = Math.max(maxTornado, s.threats?.tornado ?? 0);
        maxFlood = Math.max(maxFlood, s.threats?.flood ?? 0);
      }
    }
  }

  // Agregacja siły na podstawie obszarowego maksimum
  const severity = Math.round((maxHail + maxTornado + maxFlood * 0.8) / 2.8);

  return (
    <section className="mt-8 flex flex-col gap-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-300">
            <CloudLightning size={16} />
            Max. Szansa Inicjacji (Obszar)
          </div>
          <div className="mt-3 flex items-end gap-2">
            <span className="text-6xl font-black tabular-nums" style={{ color: riskColor(maxRisk) }}>
              {maxRisk}
            </span>
            <span className="mb-2 text-sm text-slate-500">/ 100%</span>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Najwyższe prawdopodobieństwo burzy w promieniu ~20km od wybranego punktu w ciągu 2h.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-red-500/20 bg-gradient-to-br from-red-500/5 to-purple-500/5 p-6 backdrop-blur-xl">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-red-400">
            <Skull size={16} />
            Max. Potencjał niszczący
          </div>
          <div className="mt-3 flex items-end gap-2">
            <span className="text-6xl font-black tabular-nums" style={{ color: riskColor(severity) }}>
              {severity}
            </span>
            <span className="mb-2 text-sm text-slate-500">/ 100</span>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Szacowana siła zjawiska w regionie. Skanuje sąsiednie węzły w poszukiwaniu ukrytych komórek.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ThreatTile
          title="Ulewa / Flash Flood"
          val={maxFlood}
          icon={<Waves size={20} />}
          desc="Nawalny deszcz i ryzyko zalań"
        />
        <ThreatTile
          title="Duży Grad"
          val={maxHail}
          icon={<Snowflake size={20} />}
          desc="Gradobijcie (Izoterma 0°C + CAPE)"
        />
        <ThreatTile
          title="Wichury / Tornado"
          val={maxTornado}
          icon={<Wind size={20} />}
          desc="Uskok wiatru i niska podstawa chmur"
        />
      </div>

      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-slate-300">
        <span className="font-semibold text-slate-200">Advekcja:</span> {data.trajectory.advectionLabel}
      </div>
    </section>
  );
}
/* ───────── Pomiary Rzeczywiste (Ground Truth) ───────── */
/* ───────── Weryfikacja Rzeczywista (Radar Live Proxy) ───────── */ /* ───────── Weryfikacja Rzeczywista /* ───────── Weryfikacja Rzeczywista (Radar Live Proxy) ───────── */
/* ───────── Weryfikacja Rzeczywista (Radar Live Proxy) V2 ───────── */
function LiveStormVerificationV2({ obs }: { obs: any }) {
  if (!obs) return null;

  // Wymuszenie typów, żeby JS nie przepuścił jakiegoś "nulla" jako stringa
  const precip = Number(obs.precipitation) || 0;
  const gusts = Number(obs.wind_gusts_10m) || 0;
  let rawCode = Number(obs.weather_code) || 0;

  // --- TWARDY FILTR FIZYCZNY (Zoptymalizowany) ---
  // Burza to opad LUB potężny wiatr (prądy zstępujące).
  // Jeśli opad jest zerowy i wiatr nie przekracza 50 km/h, to kod WMO jest halucynacją.
  if (precip < 0.5 && gusts < 50) {
    rawCode = 0;
  }

  const isThunderstorm = [95, 96, 99].includes(rawCode);
  const isHailing = [96, 99].includes(rawCode);
  const isHeavyRain = [65, 67, 81, 82].includes(rawCode) || precip > 5;
  const isRaining = precip > 0;

  let dbz = 0;
  if (precip > 0) {
    const a = isThunderstorm || isHeavyRain ? 400 : 200;
    const b = isThunderstorm || isHeavyRain ? 1.4 : 1.6;
    const z = a * Math.pow(precip, b);
    dbz = Math.round(10 * Math.log10(z));
  }

  let livePower = 0;
  if (dbz > 15) {
    livePower += Math.min(((dbz - 15) / 45) * 60, 60);
  }

  if (isThunderstorm) livePower += 20;
  if (isHailing) livePower += 20;

  // Wiatr (limitujemy to do sensownych wartości)
  livePower += Math.min(gusts / 2.5, 30);
  livePower = Math.min(Math.round(livePower), 100);

  const isImplicitStorm = dbz >= 45 || gusts > 65;

  let statusStr = "Brak groźnych zjawisk konwekcyjnych";
  let statusColor = "text-emerald-400";
  let icon = <CheckCircle2 size={24} className="text-emerald-400" />;

  if (isThunderstorm || isImplicitStorm) {
    statusStr = isHailing || dbz >= 55 ? "MOŻLIWA BURZA Z GRADOBICIEM!" : "MOŻLIWA  BURZA / NAWAŁNICA!";
    statusColor = "text-fuchsia-400";
    icon = <CloudLightning size={24} className="text-fuchsia-400 animate-pulse" />;
  } else if (isHeavyRain || dbz >= 35) {
    statusStr = "MOŻLIWA SILNA ULEWA / NAWALNY DESZCZ";
    statusColor = "text-red-400";
    icon = <Waves size={24} className="text-red-400" />;
  } else if (isRaining) {
    statusStr = "OPADY DESZCZU";
    statusColor = "text-cyan-400";
    icon = <Droplets size={24} className="text-cyan-400" />;
  } else if (gusts > 40) {
    statusStr = "SILNY WIATR";
    statusColor = "text-amber-400";
    icon = <Wind size={24} className="text-amber-400" />;
  }

  const dbzColor =
    dbz >= 55 ? "text-fuchsia-400" : dbz >= 40 ? "text-red-400" : dbz >= 25 ? "text-amber-400" : "text-cyan-400";

  return (
    <section className="mt-8 rounded-2xl border border-white/15 bg-white/[0.04] p-6 backdrop-blur-xl shadow-lg relative overflow-hidden">
      {livePower > 30 && (
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-transparent pointer-events-none" />
      )}
      <div className="relative z-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <Activity size={14} className={livePower > 30 ? "text-red-500 animate-pulse" : "text-cyan-400"} />
            Weryfikacja na żywo (Filtr v2)
          </div>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center shrink-0 rounded-full bg-white/5 border border-white/10">
              {icon}
            </div>
            <div>
              <div className={`text-xl font-black uppercase tracking-wide ${statusColor}`}>{statusStr}</div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
                <span>
                  Odbiciowość: <span className={`font-bold ${dbzColor}`}>{dbz} dBZ</span>
                </span>
                <span className="opacity-50">|</span>
                <span>
                  Opad: <span className="font-bold text-white">{precip} mm</span>
                </span>
                <span className="opacity-50">|</span>
                <span>
                  Wiatr: <span className="font-bold text-white">{gusts} km/h</span>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="shrink-0 w-full md:w-auto p-4 rounded-xl border border-white/10 bg-[#04060a]/50">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 text-center md:text-right">
            Obecna moc żywiołu
          </div>
          <div className="mt-1 flex items-baseline justify-center md:justify-end gap-1">
            <span className="text-5xl font-black tabular-nums" style={{ color: riskColor(livePower) }}>
              {livePower}
            </span>
            <span className="text-sm text-slate-500">/ 100</span>
          </div>
          <div className="mt-2 h-1.5 w-full md:w-32 overflow-hidden rounded-full bg-white/5 mx-auto md:ml-auto md:mr-0">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${livePower}%`, background: riskGradient(livePower) }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
function ThreatTile({ title, val, icon, desc }: { title: string; val: number; icon: React.ReactNode; desc: string }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl transition hover:bg-white/[0.05]">
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/5"
        style={{ color: riskColor(val), border: `1px solid ${riskColor(val)}40` }}
      >
        {icon}
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</div>
        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-xl font-black tabular-nums" style={{ color: riskColor(val) }}>
            {val}
          </span>
          <span className="text-[10px] text-slate-500">/ 100</span>
        </div>
        <div className="mt-0.5 text-[10px] text-slate-500">{desc}</div>
      </div>
    </div>
  );
}

/* ───────── Tabela szczegółowa modeli dla Stanicy ───────── */
function ModelsTable({ data }: { data: StormForecastResponse }) {
  const stanica = data.points.find((p) => p.pointId === "center")!;
  const horizons = [15, 30, 60, 120];

  return (
    <section className="mt-10">
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Surowe dane modeli · Stanica</h2>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl">
        <table className="min-w-[820px] w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-400">
            <tr className="border-b border-white/5">
              <th className="px-4 py-3 text-left font-medium">Model</th>
              <th className="px-4 py-3 text-left font-medium">Horyzont</th>
              <th className="px-3 py-3 text-right font-medium">CAPE</th>
              <th className="px-3 py-3 text-right font-medium">CIN</th>
              <th className="px-3 py-3 text-right font-medium">LI</th>
              <th className="px-3 py-3 text-right font-medium">Opad</th>
              <th className="px-3 py-3 text-right font-medium">Poryw</th>
              <th className="px-3 py-3 text-right font-medium">DP 2m</th>
              <th className="px-3 py-3 text-right font-medium">Lapse</th>
              <th className="px-3 py-3 text-right font-medium">Shear</th>
              <th className="px-3 py-3 text-right font-medium">RH850</th>
              <th className="px-3 py-3 text-right font-medium">LPI</th>
              <th className="px-3 py-3 text-right font-medium">Risk</th>
            </tr>
          </thead>
          <tbody>
            {MODELS.flatMap((m) => {
              return horizons.map((h) => {
                const best =
                  data.ensemble.find((e) => e.horizonMin === h)?.perModel.find((pm) => pm.model === m)?.sample ?? null;
                return (
                  <tr key={`${m}-${h}`} className="border-b border-white/5 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-200">{MODEL_META[m].label}</div>
                      <div className="text-[10px] text-slate-500">{MODEL_META[m].resolution}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400">+{h} min</td>
                    <Cell v={best?.cape} unit="J/kg" />
                    <Cell v={best?.cin} unit="J/kg" />
                    <Cell v={best?.li} unit="°C" digits={1} />
                    <Cell v={best?.precip} unit="mm" digits={1} />
                    <Cell v={best?.gust} unit="m/s" digits={1} />
                    <Cell v={best?.dewPoint} unit="°C" digits={1} />
                    <Cell v={best?.lapseRate} unit="°C/km" digits={1} />
                    <Cell
                      v={
                        best?.wind500 != null && best?.windSpeed != null
                          ? Math.abs(best.wind500 - best.windSpeed)
                          : null
                      }
                      unit="m/s"
                      digits={1}
                    />
                    <Cell v={best?.rh850} unit="%" />
                    <Cell v={best?.lightning} unit="" digits={2} />
                    <td className="px-3 py-2.5 text-right">
                      {best && best.isValid ? (
                        <span
                          className="inline-block rounded-md px-2 py-0.5 text-xs font-bold tabular-nums"
                          style={{ background: riskColor(best.risk) + "22", color: riskColor(best.risk) }}
                        >
                          {best.risk}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-500" title="Model nie pokrywa tego terytorium">
                          Brak zasięgu
                        </span>
                      )}
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Cell({ v, unit, digits = 0 }: { v: number | null | undefined; unit: string; digits?: number }) {
  if (v == null || Number.isNaN(v)) return <td className="px-3 py-2.5 text-right text-slate-600">—</td>;
  return (
    <td className="px-3 py-2.5 text-right tabular-nums text-slate-200">
      {v.toFixed(digits)}
      {unit && <span className="ml-0.5 text-[10px] text-slate-500">{unit}</span>}
    </td>
  );
}

/* ───────── Punkty otaczające ───────── */
function PointsGrid({ data }: { data: StormForecastResponse }) {
  return (
    <section className="mt-10">
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        Punkty otaczające — ryzyko +30 min (ICON-D2)
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {data.points.map((p) => {
          const samples = p.models.icon_d2 ?? p.models.icon_eu ?? [];
          let best: StormSample | null = null,
            bd = Infinity;
          for (const s of samples) {
            const d = Math.abs(s.offsetMin - 30);
            if (d < bd && d <= 35) {
              best = s;
              bd = d;
            }
          }
          const risk = best?.risk ?? 0;
          return (
            <div
              key={p.pointId}
              className={`relative overflow-hidden rounded-xl border p-4 backdrop-blur-xl ${
                p.pointId === "stanica" ? "border-cyan-400/40 bg-cyan-500/5" : "border-white/10 bg-white/[0.03]"
              }`}
            >
              <div className="text-xs font-medium text-slate-300">{p.name}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="text-3xl font-black tabular-nums" style={{ color: riskColor(risk) }}>
                  {risk}
                </div>
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-white/5"
                  title={`Trend ryzyka: ${best?.trend ?? "brak danych"}`}
                >
                  {best?.trend === "rosnie" ? (
                    <TrendingUp size={14} className="text-red-400" />
                  ) : best?.trend === "spada" ? (
                    <TrendingDown size={14} className="text-emerald-400" />
                  ) : (
                    <Minus size={14} className="text-slate-600" />
                  )}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {p.lat.toFixed(3)}°N · {p.lon.toFixed(3)}°E
              </div>
              <div
                className="absolute inset-x-0 bottom-0 h-0.5"
                style={{ width: `${risk}%`, background: riskGradient(risk) }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ───────── Wyjaśnienie formuły ───────── */
function FormulaSection() {
  return (
    <section className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-300">
        <Activity size={14} />
        Zaawansowana formuła predykcyjna v2 (Live)
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-300">
        Algorytm rozdziela <strong>szansę na inicjację</strong> od <strong>potencjału niszczącego</strong>. Podstawowe
        ryzyko tłumione jest przez inwersję (CIN), podczas gdy siła zjawiska zależy od gradientu pionowego i uskoku
        wiatru.
      </p>
      <div className="mt-4 grid gap-3 text-xs text-slate-300 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <Tile
          icon={<Zap size={14} />}
          title="CAPE & LI"
          desc="Energia potencjalna i niestabilność troposfery. Wartości >1500 J/kg zwiastują gwałtowne prądy wznoszące."
        />
        <Tile
          icon={<Droplets size={14} />}
          title="Punkt Rosy (DP 2m)"
          desc="Zapalnik burzy. Powyżej 16°C oznacza ogromną ilość pary wodnej przy ziemi gotowej do kondensacji."
        />
        <Tile
          icon={<TrendingUp size={14} />}
          title="Lapse Rate"
          desc="Pionowy gradient temperatury (850-500hPa). >7°C/km powoduje ekstremalnie szybki rozwój kowadła burzowego."
        />
        <Tile
          icon={<Wind size={14} />}
          title="Shear (Uskok)"
          desc="Różnica wiatru. Organizuje burzę w superkomórkę, wydłużając jej życie i generując zagrożenie tornadem."
        />
        <Tile
          icon={<AlertTriangle size={14} />}
          title="CIN (Blokada)"
          desc="Hamulec inwersyjny. Skutecznie blokuje rozwój chmur nawet przy ogromnym CAPE, redukując ryzyko."
        />
        <Tile
          icon={<Snowflake size={14} />}
          title="Izoterma 0°C"
          desc="Wysokość zamarzania. Kluczowa dla formuły liczącej prawdopodobieństwo wystąpienia dużego gradu (optymalnie ~3km)."
        />
      </div>
    </section>
  );
}

function Tile({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
        <span className="text-cyan-300">{icon}</span>
        {title}
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{desc}</p>
    </div>
  );
}

/* ───────── helpers UI ───────── */
function riskColor(r: number) {
  if (r < 15) return "#4ade80";
  if (r < 35) return "#fde047";
  if (r < 60) return "#fb923c";
  if (r < 80) return "#f87171";
  return "#e879f9";
}
function riskGradient(r: number) {
  return `linear-gradient(90deg, ${riskColor(Math.max(0, r - 20))}, ${riskColor(r)})`;
}

function SkeletonBlock() {
  return (
    <div className="mt-10 space-y-4">
      <div className="h-24 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-36 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]" />
    </div>
  );
}
/* ───────── Interaktywny Radar (Prawdziwy Ground Truth) ───────── */
function LiveRadarWidget({ lat, lon, name }: { lat: number; lon: number; name: string }) {
  return (
    <section className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl shadow-lg">
      <div className="flex items-center gap-2 border-b border-white/5 bg-white/[0.04] px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-300">
        <Activity size={14} className="text-cyan-400" />
        Faktyczne Echo Radarowe (Pomiary satelitarne i naziemne) · {name}
      </div>

      <div className="relative w-full h-[60vw] min-h-[320px] max-h-[520px] bg-slate-900">
        <iframe
          title={`Radar opadów · ${name}`}
          // Zmieniliśmy link z powrotem na stabilne embed.html z wymuszonym overlay=radar i marker=true
          src={`https://embed.windy.com/embed.html?type=map&location=coordinates&metricRain=mm&metricTemp=%C2%B0C&metricWind=km/h&zoom=8&overlay=radar&product=radar&level=surface&lat=${lat}&lon=${lon}&marker=true`}
          frameBorder="0"
          loading="lazy"
          className="absolute inset-0 w-full h-full block"
        />
      </div>

      <div className="bg-[#04060a]/80 px-6 py-3 text-[11px] text-slate-500">
        Jeśli Modele NWP (powyżej) pokazują brak zagrożenia, a na radarze widzisz czerwone/fioletowe struktury, oznacza
        to, że model nie zainicjował prawidłowo komórki burzowej.{" "}
        <strong className="text-slate-300">Zawsze ufaj radarowi.</strong>
      </div>
    </section>
  );
}
/* ───────── Wyszukiwarka Lokalizacji (Open-Meteo Geocoding) ───────── */
function LocationSearch({ onSelect }: { onSelect: (loc: { lat: number; lon: number; name: string }) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 3) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=pl&format=json`,
        );
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || []);
        }
      } catch (e) {
        console.error("Geocoding error", e);
      } finally {
        setIsSearching(false);
      }
    }, 500); // debounce 500ms
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative z-50">
      <div className="flex items-center gap-2 rounded-2xl border border-white/20 bg-white/5 px-4 py-3 backdrop-blur-xl transition focus-within:border-cyan-400/50">
        <Search size={18} className="text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj innej miejscowości (np. Warszawa, Katowice)..."
          className="w-full bg-transparent text-sm text-white placeholder-slate-500 outline-none"
        />
        {isSearching && <Loader2 size={16} className="animate-spin text-cyan-400" />}
      </div>

      {results.length > 0 && (
        <ul className="absolute top-full mt-2 w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0f1219] shadow-2xl">
          {results.map((r: any) => (
            <li key={r.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-white/5"
                onClick={() => {
                  onSelect({ lat: r.latitude, lon: r.longitude, name: r.name });
                  setQuery("");
                  setResults([]);
                }}
              >
                <MapPin size={16} className="text-cyan-400 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-slate-200">{r.name}</div>
                  <div className="text-xs text-slate-500">
                    {r.admin1 && `${r.admin1}, `}
                    {r.country} · {r.latitude.toFixed(2)}°N, {r.longitude.toFixed(2)}°E
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
